import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { capitalizeText } from "helpers/methods";
import editorTokens from "../SignRequestEditor.tokens";
import { PlusIcon, SearchIcon } from "./EditorIcons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  addSigner,
  ANY_SIGNER,
  ANY_SIGNER_COLOR,
  fieldColor,
  Fields,
  getFieldSize,
  getNextAvailableId,
  getNextRadioGroupId,
  customFieldDefaults,
  customFieldKey,
  findCustomFieldByKey,
  libraryFieldProps,
  paletteTypeOf,
  PDF_VIEWER_PAGE_SELECTOR,
} from "../SignRequestHelper";
import Field from "./Field";
import CustomFieldModal from "./CustomFieldModal";
import useCustomFieldLibrary from "./useCustomFieldLibrary";
import { ConfirmModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { UseDocument } from "./UseDocument";
import { CustomField, FileField } from "models/interfaces";
import {
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  setSelectedField as setSelectedFieldRedux,
  updateFileInSignRequest,
} from "redux/appSlice";
import { RootState } from "utils/store";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectDefaultDateFormat } from "redux/preferencesSlice";

const { leftRail, palette, scrollbar } = editorTokens;

const FieldsPanel = ({
  setIsFieldWithinBounds,
  setPreviewCoords,
  selectedField,
  setSelectedField,
}: {
  setIsFieldWithinBounds: (value: boolean) => void;
  setPreviewCoords: (value: { x: number; y: number }) => void;
  selectedField: string | null | undefined;
  setSelectedField: (value: string | null) => void;
}) => {
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const { isWithinPageBounds, getPage } = UseDocument();
  const { selectedFile, selectedParticipant, signRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);
  const {
    customFields,
    save: saveCustomField,
    remove: removeCustomField,
    canManage: canManageCustomFields,
  } = useCustomFieldLibrary();
  const [customFieldModalOpen, setCustomFieldModalOpen] = useState(false);
  const [editingCustomField, setEditingCustomField] =
    useState<CustomField | null>(null);
  const [deletingCustomField, setDeletingCustomField] =
    useState<CustomField | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const [initialDragPosition, setInitialDragPosition] = useState({
    x: 0,
    y: 0,
  });

  const [fieldSearch, setFieldSearch] = useState("");

  const visibleFieldGroups = React.useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    return Object.entries(Fields(theme))
      .map(([key, value]) => {
        if (!query) return [key, value] as const;
        const matches = (value as any[]).filter((field: any) =>
          capitalizeText(String(field.type).replace(/_/g, " "))
            .toLowerCase()
            .includes(query),
        );
        return [key, matches] as const;
      })
      .filter(([, value]) => (value as any[]).length > 0);
  }, [fieldSearch, theme]);

  /**
   * "Frequently used" in the design is a two-up shortcut to Signature and Date
   * signed — the same entries as the FIELDS list, surfaced first.
   */
  const frequentFields = React.useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    const shortcuts = [strings.signature, strings.dateSigned];
    return Fields(theme)
      .Fields.filter((field: any) => shortcuts.includes(field.type))
      .filter(
        (field: any) =>
          !query ||
          capitalizeText(String(field.type).replace(/_/g, " "))
            .toLowerCase()
            .includes(query),
      );
  }, [fieldSearch, theme]);

  const dragDistance = useRef(0);

  const fieldBounds = useRef({
    height: 0,
    width: 0,
  });

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      const $page = getPage(event, PDF_VIEWER_PAGE_SELECTOR);
      const isWithin = isWithinPageBounds(
        event,
        PDF_VIEWER_PAGE_SELECTOR,
        $page,
        fieldBounds.current.width,
        fieldBounds.current.height,
      );

      setIsFieldWithinBounds(isWithin);

      const DRAG_PREVIEW_SIZE = 36;
      setPreviewCoords({
        x: event.clientX - DRAG_PREVIEW_SIZE / 2,
        y: event.clientY - DRAG_PREVIEW_SIZE / 2,
      });

      if (initialDragPosition.x && initialDragPosition.y) {
        const dx = event.clientX - initialDragPosition.x;
        const dy = event.clientY - initialDragPosition.y;
        dragDistance.current = Math.sqrt(dx * dx + dy * dy);
      }
    },
    [isWithinPageBounds, initialDragPosition],
  );

  const onMouseClick = useCallback(
    async (event: MouseEvent) => {
      if (!selectedField) {
        return;
      }
      const $page = getPage(event, PDF_VIEWER_PAGE_SELECTOR);

      if (!$page) {
        setSelectedField(null);
        setInitialDragPosition({ x: 0, y: 0 });
        return;
      }

      const pageNumber = parseInt(
        $page.getAttribute("data-page-number") ?? "1",
        10,
      );

      const { top, left, height, width } = $page.getBoundingClientRect();

      const placementIndicator = document.createElement("div");
      const pageX = event.clientX - left;
      const pageY = event.clientY - top;

      placementIndicator.style.position = "absolute";
      placementIndicator.style.top = `${
        pageY - fieldBounds.current.height / 2
      }px`;
      placementIndicator.style.left = `${
        pageX - fieldBounds.current.width / 2
      }px`;
      $page.appendChild(placementIndicator);

      // Use clientX/clientY which are viewport-relative to match getBoundingClientRect
      let fieldPageX = ((event.clientX - left) / width) * 100;
      let fieldPageY = ((event.clientY - top) / height) * 100;

      // A drag from the Custom Fields panel carries `custom:<id>`; the field is
      // placed as the definition's type with its saved config copied in.
      const definition = findCustomFieldByKey(selectedField, customFields);
      const fieldType = definition?.fieldType ?? selectedField;

      const [fieldW, fieldH] = getFieldSize(fieldType);

      const fieldPageWidth = (fieldW / width) * 100;
      const fieldPageHeight = (fieldH / height) * 100;

      fieldPageX -= fieldPageWidth / 2;
      fieldPageY -= fieldPageHeight / 2;

      // Clamp drop location so edge drops still place the field fully inside page.
      const maxX = Math.max(0, 100 - fieldPageWidth);
      const maxY = Math.max(0, 100 - fieldPageHeight);
      fieldPageX = Math.min(Math.max(fieldPageX, 0), maxX);
      fieldPageY = Math.min(Math.max(fieldPageY, 0), maxY);

      const isRadio = fieldType === strings.radioButton;
      const radioGroupId = isRadio
        ? getNextRadioGroupId(selectedFile)
        : undefined;
      const radioOptionIndex = 1;
      const sameTypeCount = selectedFile.fields.filter(
        (f: FileField) =>
          f.type === fieldType && f.signerIndex === selectedParticipant.id,
      ).length;
      const labelSuffix = sameTypeCount + 1;

      let newField: any = {
        id: getNextAvailableId(selectedFile),
        label: isRadio
          ? `Option ${radioOptionIndex}`
          : (() => {
              const baseName =
                selectedParticipant.name ||
                selectedParticipant.requestRole ||
                "Signer";
              const firstName = baseName.includes(" ")
                ? baseName.split(" ")[0]
                : baseName;
              return `${firstName}-${fieldType?.split(" ").join("_").toLowerCase()}-${labelSuffix}`;
            })(),
        type: fieldType,
        frameX: width,
        frameY: height,
        x: fieldPageX,
        y: fieldPageY,
        w: fieldPageWidth,
        h: fieldPageHeight,
        fieldPageX: fieldPageX,
        fieldPageY: fieldPageY,
        fieldPageWidth,
        fieldPageHeight,
        width: width,
        height: height,
        left: left,
        top: top,
        page: pageNumber - 1,
        color: fieldColor[selectedParticipant.id],
        signerIndex: selectedParticipant.id,
        maxLength: 200,
        required: fieldType !== strings.checkbox,
        ...(isRadio
          ? { groupId: radioGroupId, value: "false", required: true }
          : {}),
        ...(fieldType === strings.date ||
        fieldType === strings.dateSigned
          ? { dateFormat: defaultDateFormat }
          : {}),
        hidden: false,
        // readOnly: false,
        readOnly: fieldType === strings.email,
        value:
          fieldType === strings.email
            ? selectedParticipant.email || ""
            : undefined,
        tabLabel: "",
        toolTip: "",
        validationPattern: "none",
        validationMessage: "",
        placeholder: "",
        multiLine: false,
        styles: {
          fontFamily: "Times",
          color: "#1A1A1A",
          size: 16,
          fontWeight: {
            bold: false,
            italic: false,
            underline: false,
          },
        },
        existingField: false,
        // Only a field placed from the Custom Fields section carries the custom-field
        // properties; a Fields-panel drag keeps exactly the shape it had before.
        ...(definition
          ? {
              ...customFieldDefaults(fieldType),
              ...libraryFieldProps(definition, signRequest?.selfSigned === true),
            }
          : {}),
      };
      if (definition?.config?.signerIndex === ANY_SIGNER) {
        newField.signerIndex = ANY_SIGNER;
        newField.color = ANY_SIGNER_COLOR;
      }
      const updatedFile = {
        ...selectedFile,
        fields: [...selectedFile.fields, newField],
      };
      dispatch(setReduxSelectedFile(updatedFile));
      dispatch(updateFileInSignRequest(updatedFile));
      // Select the just-placed field. Previously this happened implicitly when
      // DraggableField's coord-calc dispatched setSelectedField; that side effect
      // was removed (it drove a re-render loop), so select it explicitly here.
      dispatch(setSelectedFieldRedux(newField));
      setIsFieldWithinBounds(false);
      setSelectedField(null);
      setInitialDragPosition({ x: 0, y: 0 });
    },
    [
      isWithinPageBounds,
      selectedField,
      getPage,
      selectedParticipant,
      selectedFile,
      customFields,
    ],
  );

  useEffect(() => {
    if (selectedField) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseClick);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseClick);
    };
  }, [onMouseClick, onMouseMove, selectedField]);

  useEffect(() => {
    if (!selectedField) return;
    const [fieldW, fieldH] = getFieldSize(
      paletteTypeOf(selectedField, customFields),
    );
    fieldBounds.current = {
      width: fieldW,
      height: fieldH,
    };
  }, [selectedField, customFields]);

  useEffect(() => {
    if (signRequest.signers.length === 0) {
      addSigner(selectedParticipant, signRequest.signers, dispatch);
    }
    if (
      !selectedParticipant ||
      (!selectedParticipant.name &&
        !selectedParticipant.requestRole &&
        !selectedParticipant.email)
    ) {
      dispatch(setReduxSelectedParticipant(undefined));
    }
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes fieldDragStart {
        0% { transform: scale(1); }
        50% { transform: scale(0.9); }
        100% { transform: scale(1); }
      }
      .field-dragging {
        animation: fieldDragStart 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const eyebrowSx = {
    fontSize: leftRail.eyebrowFontSize,
    fontWeight: leftRail.eyebrowFontWeight,
    letterSpacing: leftRail.eyebrowLetterSpacing,
    textTransform: "uppercase" as const,
    lineHeight: 1.4,
    color: leftRail.eyebrowColor,
    mb: leftRail.groupLabelMarginBottom,
  };

  // Pick the PDF page currently closest to the centre of the viewport, so a
  // keyboard-inserted field lands on the page the user is actually looking at.
  const pickTargetPage = (): HTMLElement | null => {
    const pages = Array.from(
      document.querySelectorAll<HTMLElement>(PDF_VIEWER_PAGE_SELECTOR),
    );
    if (pages.length === 0) return null;
    const viewportCenter = window.innerHeight / 2;
    let best = pages[0];
    let bestDist = Infinity;
    pages.forEach((p) => {
      const r = p.getBoundingClientRect();
      const dist = Math.abs(r.top + r.height / 2 - viewportCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = p;
      }
    });
    return best;
  };

  // Keyboard equivalent of dragging a palette field onto the page (WCAG 2.1.1).
  // Inserts the chosen field type at the centre of the page in view, then
  // selects it so the user can immediately nudge it (arrow keys) or edit it in
  // the properties panel. Mirrors the field object built in `onMouseClick` so
  // the mouse-drop path is left completely untouched.
  const insertFieldAtDefault = (paletteKey: string) => {
    const definition = findCustomFieldByKey(paletteKey, customFields);
    const fieldType = definition?.fieldType ?? paletteKey;
    if (!fieldType || !selectedFile) return;
    if (!selectedParticipant || selectedParticipant.id == null) return;
    const $page = pickTargetPage();
    if (!$page) return;

    const pageNumber = parseInt(
      $page.getAttribute("data-page-number") ?? "1",
      10,
    );
    const { top, left, height, width } = $page.getBoundingClientRect();
    const [fieldW, fieldH] = getFieldSize(fieldType);
    const fieldPageWidth = (fieldW / width) * 100;
    const fieldPageHeight = (fieldH / height) * 100;
    const maxX = Math.max(0, 100 - fieldPageWidth);
    const maxY = Math.max(0, 100 - fieldPageHeight);
    const fieldPageX = Math.min(Math.max(50 - fieldPageWidth / 2, 0), maxX);
    const fieldPageY = Math.min(Math.max(50 - fieldPageHeight / 2, 0), maxY);

    const isRadio = fieldType === strings.radioButton;
    const radioGroupId = isRadio
      ? getNextRadioGroupId(selectedFile)
      : undefined;
    const sameTypeCount = selectedFile.fields.filter(
      (f: FileField) =>
        f.type === fieldType && f.signerIndex === selectedParticipant.id,
    ).length;
    const labelSuffix = sameTypeCount + 1;

    const newField: any = {
      id: getNextAvailableId(selectedFile),
      label: isRadio
        ? "Option 1"
        : (() => {
            const baseName =
              selectedParticipant.name ||
              selectedParticipant.requestRole ||
              "Signer";
            const firstName = baseName.includes(" ")
              ? baseName.split(" ")[0]
              : baseName;
            return `${firstName}-${fieldType
              ?.split(" ")
              .join("_")
              .toLowerCase()}-${labelSuffix}`;
          })(),
      type: fieldType,
      frameX: width,
      frameY: height,
      x: fieldPageX,
      y: fieldPageY,
      w: fieldPageWidth,
      h: fieldPageHeight,
      fieldPageX,
      fieldPageY,
      fieldPageWidth,
      fieldPageHeight,
      width,
      height,
      left,
      top,
      page: pageNumber - 1,
      color: fieldColor[selectedParticipant.id],
      signerIndex: selectedParticipant.id,
      maxLength: 200,
      required: fieldType !== strings.checkbox,
      ...(isRadio
        ? { groupId: radioGroupId, value: "false", required: true }
        : {}),
      ...(fieldType === strings.date || fieldType === strings.dateSigned
        ? { dateFormat: defaultDateFormat }
        : {}),
      hidden: false,
      readOnly: fieldType === strings.email,
      value:
        fieldType === strings.email
          ? selectedParticipant.email || ""
          : undefined,
      tabLabel: "",
      toolTip: "",
      validationPattern: "none",
      validationMessage: "",
      placeholder: "",
      multiLine: false,
      styles: {
        fontFamily: "Times",
        color: "#1A1A1A",
        size: 16,
        fontWeight: { bold: false, italic: false, underline: false },
      },
      existingField: false,
      // Only a field placed from the Custom Fields section carries the custom-field
      // properties; a Fields-panel drag keeps exactly the shape it had before.
      ...(definition
        ? {
            ...customFieldDefaults(fieldType),
            ...libraryFieldProps(definition, signRequest?.selfSigned === true),
          }
        : {}),
    };
    if (definition?.config?.signerIndex === ANY_SIGNER) {
      newField.signerIndex = ANY_SIGNER;
      newField.color = ANY_SIGNER_COLOR;
    }

    const updatedFile = {
      ...selectedFile,
      fields: [...selectedFile.fields, newField],
    };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
    dispatch(setSelectedFieldRedux(newField));
  };

  const renderField = (
    field: any,
    variant: "row" | "quick",
    showDragHandle: boolean,
  ) => (
    <Field
      key={`${variant}-${field.type}`}
      field={field}
      variant={variant}
      showDragHandle={showDragHandle}
      dragDistance={dragDistance}
      setDragStartPosition={setInitialDragPosition}
      setSelectedField={setSelectedField}
      onKeyboardInsert={insertFieldAtDefault}
    />
  );

  // Saved definitions drag exactly like palette rows; the row carries the
  // definition's name and its type's glyph, and the drag key resolves back to
  // the definition when dropped.
  const visibleCustomFields = React.useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    return customFields.filter(
      (field) => !query || field.name.toLowerCase().includes(query),
    );
  }, [customFields, fieldSearch]);

  const openCreateCustomField = () => {
    setEditingCustomField(null);
    setCustomFieldModalOpen(true);
  };

  const openEditCustomField = (field: CustomField) => {
    setEditingCustomField(field);
    setCustomFieldModalOpen(true);
  };

  const confirmDeleteCustomField = async () => {
    if (!deletingCustomField?.id) return;
    setDeletingBusy(true);
    const removed = await removeCustomField(deletingCustomField.id);
    setDeletingBusy(false);
    if (removed) setDeletingCustomField(null);
  };

  const renderCustomField = (field: CustomField) => (
    <Field
      key={`custom-${field.id}`}
      field={{
        type: customFieldKey(field.id as string),
        iconType: field.fieldType,
        name: field.name,
        description: field.description,
      }}
      variant="row"
      showDragHandle
      dragDistance={dragDistance}
      setDragStartPosition={setInitialDragPosition}
      setSelectedField={setSelectedField}
      onKeyboardInsert={insertFieldAtDefault}
      onEdit={canManageCustomFields ? () => openEditCustomField(field) : undefined}
      onDelete={
        canManageCustomFields ? () => setDeletingCustomField(field) : undefined
      }
    />
  );

  return (
    <>
      <Box sx={{ flexShrink: 0, p: leftRail.searchPadding }}>
        <TextField
          variant="outlined"
          value={fieldSearch}
          onChange={(event) => setFieldSearch(event.target.value)}
          placeholder="Search fields…"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Search fields" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0, ml: 0 }}>
                <Box
                  component="span"
                  sx={{ display: "flex", color: palette.muted }}
                >
                  <SearchIcon size={leftRail.searchIconSize} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: leftRail.searchHeight,
              borderRadius: leftRail.searchRadius,
              p: leftRail.searchBoxPadding,
              gap: leftRail.searchGap,
              boxSizing: "border-box",
              backgroundColor: palette.surface,
              border: `1px solid ${palette.borderLight}`,
              "& fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
            },
            "& .MuiOutlinedInput-input": {
              p: 0,
              height: "100%",
              fontSize: leftRail.searchFontSize,
              color: palette.ink,
              lineHeight: "normal",
              "&::placeholder": { color: palette.muted, opacity: 1 },
            },
          }}
        />
      </Box>

      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          p: 0,
          m: -0.125,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {fieldSearch.trim()
          ? (() => {
              const count = visibleFieldGroups.reduce(
                (sum, [, value]) => sum + (value as any[]).length,
                0,
              );
              return count === 0
                ? "No fields found"
                : `${count} field${count === 1 ? "" : "s"} found`;
            })()
          : ""}
      </Box>

      <Box
        id="draggable-fields"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: leftRail.listPadding,
          pr: leftRail.listPaddingRight,
          "&::-webkit-scrollbar": { width: scrollbar.size },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scrollbar.thumb,
            border: scrollbar.thumbBorder,
            backgroundClip: "content-box",
            borderRadius: scrollbar.radius,
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {frequentFields.length > 0 && (
          <Box sx={{ mb: leftRail.sectionMarginBottom }}>
            <Typography component="h3" sx={eyebrowSx}>
              Frequently used
            </Typography>
            <Box
              sx={{
                display: "grid",
                // `minmax(0, …)` so a long field name can't push its column
                // past half the rail.
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: leftRail.quickGap,
              }}
            >
              {frequentFields.map((field: any) =>
                renderField(field, "quick", false),
              )}
            </Box>
          </Box>
        )}

        {visibleFieldGroups.map(([key, value], index: number) => (
          <Box
            key={key}
            sx={{
              mb:
                index < visibleFieldGroups.length - 1
                  ? leftRail.sectionMarginBottom
                  : 0,
            }}
          >
            <Typography component="h3" sx={eyebrowSx}>
              {key}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: leftRail.rowGap,
              }}
            >
              {(value as any[]).map((field: any) =>
                renderField(field, "row", key === "Fields"),
              )}
            </Box>
          </Box>
        ))}

        {(!fieldSearch.trim() || visibleCustomFields.length > 0) && (
          <Box sx={{ mt: leftRail.sectionMarginBottom }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: leftRail.groupLabelMarginBottom,
              }}
            >
              <Typography component="h3" sx={{ ...eyebrowSx, mb: 0 }}>
                Custom Fields
              </Typography>
              {canManageCustomFields && (
                <Button
                  size="small"
                  variant="text"
                  onClick={openCreateCustomField}
                  startIcon={<PlusIcon size={14} />}
                  aria-label="Create custom field"
                  sx={{
                    textTransform: "none",
                    color: "primary.main",
                    fontSize: leftRail.rowFontSize,
                    minWidth: 0,
                    p: 0,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  Create
                </Button>
              )}
            </Box>
            {visibleCustomFields.length === 0 ? (
              <Typography
                sx={{
                  fontSize: leftRail.rowFontSize,
                  color: palette.muted,
                  lineHeight: 1.5,
                }}
              >
                Create a field once — name, validation, options — and drag it
                onto any document.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: leftRail.rowGap,
                }}
              >
                {visibleCustomFields.map(renderCustomField)}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <CustomFieldModal
        open={customFieldModalOpen}
        customField={editingCustomField}
        existingNames={customFields.map((field) => field.name)}
        onClose={() => setCustomFieldModalOpen(false)}
        onSave={saveCustomField}
      />

      <ConfirmModal
        open={!!deletingCustomField}
        onClose={() => setDeletingCustomField(null)}
        title="Delete custom field?"
        message={`"${deletingCustomField?.name ?? ""}" will be removed from the Custom Fields panel. Fields already placed on documents are not affected.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={deletingBusy}
        onConfirm={confirmDeleteCustomField}
      />
    </>
  );
};

export default FieldsPanel;
